/**
 * Pure formatting for Admin → Login Activity: org pills, session length, the
 * "Active" rule and the one-line "Used" summary of a session.
 */
import { DASHBOARDS } from '../../../config/dashboards';
import type { ActivitySession, LoginEvent } from '../../../services/adminApi';
import type { StatusVariant } from '../../ui';

const MIN = 60 * 1000;

/** Pings come at most every 10 minutes and date themselves to the last input,
 *  so someone working right now can look up to ~20 minutes idle. */
export const ACTIVE_WINDOW_MS = 20 * MIN;

export const orgLabel = (org: LoginEvent['organization']): string => {
  if (org === 'culturegenesis') return 'CG';
  if (org === 'partner') return 'Partner';
  return 'External';
};

export const orgVariant = (org: LoginEvent['organization']): StatusVariant => {
  if (org === 'culturegenesis') return 'ok';
  if (org === 'partner') return 'partner';
  return 'draft';
};

/** The registry label, or a readable fallback for an id it no longer lists. */
export function dashboardLabel(id: string): string {
  const entry = DASHBOARDS.find((d) => d.id === id);
  if (entry) return entry.label;
  const words = id.split('-').filter(Boolean).join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

/** Tool titles carry a parenthetical for Claude ("Campaign snapshot (one order)"); the summary drops it. */
export function shortToolTitle(title: string): string {
  return title.replace(/\s*\([^)]*\)\s*$/, '') || title;
}

/** "<1 min", "12 min", "1 h 05 min", "3 h". */
export function formatSessionLength(startMillis: number, endMillis: number): string {
  const minutes = Math.floor(Math.max(0, endMillis - startMillis) / MIN);
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')} min`;
}

export function isActiveNow(session: Pick<ActivitySession, 'lastActiveAtMillis'>, now: number = Date.now()): boolean {
  return now - session.lastActiveAtMillis <= ACTIVE_WINDOW_MS;
}

/** Where the session happened: "Web", or the Claude client ("Claude", "Claude Code"). */
export function whereLabel(session: ActivitySession): string {
  if (session.surface === 'web') return 'Web';
  return session.connector?.client || 'Claude connector';
}

const times = (n: number) => (n > 1 ? ` ×${n}` : '');

/**
 * The "Used" column: the first `max` dashboards (web) or tools (connector),
 * and how many more there are. Connector sessions lead with the call count.
 */
export function usedSummary(
  session: ActivitySession,
  max = 3,
): { lead: string | null; items: string[]; more: number; failed: number } {
  if (session.surface === 'connector') {
    const items = session.tools.slice(0, max).map((t) => `${shortToolTitle(t.title)}${times(t.calls)}`);
    const calls = session.toolCalls;
    return {
      lead: `${calls} ${calls === 1 ? 'call' : 'calls'}`,
      items,
      more: Math.max(0, session.tools.length - max),
      failed: session.toolErrors,
    };
  }
  const items = session.dashboards.slice(0, max).map((d) => `${dashboardLabel(d.id)}${times(d.opens)}`);
  return { lead: null, items, more: Math.max(0, session.dashboards.length - max), failed: 0 };
}
